import { useContext, useMemo } from "react";
import { PresenceKind } from "@common/types/presence-nats";
import type { PresenceViewersTarget } from "@hooks/presence/usePresenceViewers";
import { ScopeContext, useScope } from "@state/scopeContext.lib";

/**
 * The organization whose tasks the page shows across its workspaces (whose
 * viewers are its staff), or null on a workspace's page.
 */
export const useTasksOrganizationId = (): string | null => {
    const { organizationId } = useScope();
    const { workspaceId: appWorkspaceId } = useContext(ScopeContext);
    return organizationId && !appWorkspaceId ? organizationId : null;
};

/**
 * The presence target of a task's viewers: the organization's channel on
 * organization-wide pages, else the task's workspace's.
 */
export const useTaskViewersTarget = ({
    taskId,
    workspaceId,
}: {
    taskId: string;
    workspaceId: string;
}): PresenceViewersTarget => {
    const organizationId = useTasksOrganizationId();

    return useMemo(
        () => ({
            scope: organizationId ? { organizationId } : { workspaceId },
            kind: PresenceKind.TASK,
            workspaceId,
            targetId: taskId,
        }),
        [organizationId, workspaceId, taskId],
    );
};
